// Offers on Earn: offers members start straight from the Earn page, read from each network's offers
// API for each member (apps/api/src/lib/offers.ts). "Offers" shows what members did with each one
// and lets staff hide it or move it up or down the list; "Setup" holds each network's API account
// (the key is write-only), checks that the coins shown match what postbacks credit, and previews
// what a member in one country on one device sees right now.
import { useId, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  Check,
  CircleAlert,
  EyeOff,
  KeyRound,
  Minus,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  TriangleAlert,
} from 'lucide-react';
import { api } from '../lib/api';
import { useCan } from '../lib/staff';
import {
  Card,
  Drawer,
  EmptyState,
  ErrorNote,
  fmtNum,
  fmtUsd,
  Initial,
  PageHeader,
  Pill,
  ScrollX,
  SearchInput,
  SkeletonRows,
  Tabs,
  TimeAgo,
  toast,
  useDebounced,
} from '../components/ui';
import { Field, Segmented } from '../components/WallForm';

/* ------------------------------------------------------------------ Shapes */

interface OffersApi {
  adapter: 'edgecash';
  enabled: boolean;
  publisherId: string;
  hasKey: boolean;
  supportUrl?: string;
  casino?: boolean;
  coinsPerUsd?: number;
}
interface Network {
  id: string;
  name: string;
  status: 'active' | 'paused' | 'disabled';
  logoUrl: string | null;
  api: OffersApi | null;
  defaultSupportUrl: string;
  checks: {
    coinsPerUsd: number;
    networkCoinsPerUsd: number;
    amountIs: 'coins' | 'usd' | 'usd_cents';
    postbackOfferId: boolean;
    postbackRevenue: boolean;
    postbackClickId: boolean;
    observed: { credits: number; coinsPerUsd: number } | null;
    starts30d: number;
    credits30d: number;
    creditsWithOffer30d: number;
  };
}
interface Stats {
  starts: number;
  starters: number;
  earners: number;
  coins: number;
  ups: number;
  downs: number;
  problems: number;
  usedBefore: number;
}
interface OfferRow {
  provider: string;
  network: string;
  ref: string;
  title: string;
  iconUrl: string | null;
  packageId: string | null;
  platform: string | null;
  category: string | null;
  usd: number;
  coins: number;
  lastAt: number;
  stats: Stats;
  hidden: boolean;
  boost: number;
}
interface OfferDetail {
  offer: {
    providerId: string;
    ref: string;
    network: string;
    title: string;
    iconUrl: string | null;
    packageId: string | null;
    platform: string | null;
    category: string | null;
    usd: number;
    steps: { id: string; label: string; usd: number; coins: number }[];
    firstAt: number;
    lastAt: number;
  };
  stats: (Stats & { updatedAt: number }) | null;
  override: { hidden: boolean; boost: number; note: string | null; updatedAt: number } | null;
  usedBefore90d: number;
  credits: { event: string; n: number; coins: number; reversed: number }[];
  feedback: {
    userId: string;
    username: string | null;
    verdict: 'up' | 'down';
    reasons: string[];
    note: string | null;
    updatedAt: number;
  }[];
}
interface PreviewOffer {
  ref: string;
  title: string;
  iconUrl: string | null;
  category: string;
  platform: string;
  packageId: string | null;
  coins: number;
  usd: number;
  about: string;
  steps: { label: string; source: string; coins: number; usd: number; minutes: number | null }[];
  stats: Omit<Stats, 'starters' | 'coins'> | null;
  hidden: boolean;
  boost: number;
  note: string | null;
}
interface Preview {
  ms: number;
  received: number;
  casinoHeld: number;
  offers: PreviewOffer[];
}

const CATEGORY: Record<string, string> = {
  games: 'Game',
  apps: 'App',
  surveys: 'Survey',
  shopping: 'Shopping',
  trials: 'Free trial',
  signups: 'Sign-up',
  finance: 'Finance',
  videos: 'Videos',
  casino: 'Casino',
  other: 'Other',
};
const PLATFORM: Record<string, string> = {
  android: 'Android',
  ios: 'iPhone',
  desktop: 'Desktop',
  any: 'Any device',
};
/** Members' reasons, as the Earn page offers them (core OFFER_FEEDBACK_REASONS). */
const REASON: Record<string, string> = {
  paid_fast: 'Paid fast',
  easy: 'Easy',
  fun: 'Fun',
  no_coins: 'Coins never came',
  not_tracked: 'Didn’t track',
  too_hard: 'Too hard',
  took_long: 'Took too long',
  too_many_ads: 'Too many ads',
  app_broken: 'App didn’t work',
  misleading: 'Not as described',
};
const PROBLEM_REASONS = new Set(['no_coins', 'not_tracked']);
const BOOSTS = [
  { key: '0.5', label: 'Lower' },
  { key: '1', label: 'As ranked' },
  { key: '1.5', label: 'Higher' },
  { key: '2', label: 'Top' },
] as const;
const boostLabel = (b: number) =>
  b === 1 ? null : b < 1 ? 'Moved down' : b >= 2 ? 'Moved to top' : 'Moved up';

const pct = (n: number, of: number) => (of ? `${Math.round((n / of) * 100)}%` : '—');

/* ------------------------------------------------------------------ Page */

export function Offers() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'setup' ? 'setup' : 'offers';
  const networks = useQuery({
    queryKey: ['offers', 'networks'],
    queryFn: () => api<{ networks: Network[] }>('/offers/networks'),
  });
  const all = networks.data?.networks ?? [];
  const on = all.filter((n) => n.api?.enabled);
  // Only EdgeCash has an offers API adapter so far.
  const hasAdapter = (n: Network) => !!n.api || /edge\s*cash/i.test(`${n.id} ${n.name}`);
  const withApi = all.filter(hasAdapter);
  const others = all.filter((n) => !hasAdapter(n));
  return (
    <div className="space-y-5">
      <PageHeader
        title="Offers on Earn"
        description="Offers members start straight from the Earn page, without opening a wall. Each network’s offers API is read for each member, in their country and on their device; the list is ranked by pay and by what members did with each offer."
      />
      <div className="flex flex-wrap items-center gap-3">
        <Tabs
          label="Section"
          value={tab}
          onChange={(k) => setParams(k === 'setup' ? { tab: 'setup' } : {}, { replace: true })}
          items={[
            { key: 'offers', label: 'Offers' },
            { key: 'setup', label: 'Setup' },
          ]}
        />
        {networks.data && (
          <span className="text-[13px] text-stone">
            {on.length
              ? `On for ${on.map((n) => n.name).join(', ')}`
              : 'Not on for any network yet'}
          </span>
        )}
      </div>
      <ErrorNote error={networks.error} onRetry={() => void networks.refetch()} />
      {tab === 'offers' ? (
        <OfferList networks={networks.data?.networks ?? []} />
      ) : networks.isLoading ? (
        <div className="card">
          <SkeletonRows rows={3} />
        </div>
      ) : (
        <div className="space-y-6">
          {withApi.map((n) => (
            <NetworkSetup key={n.id} n={n} />
          ))}
          {withApi.length === 0 && (
            <div className="card">
              <EmptyState icon={Sparkles} title="No EdgeCash network yet">
                Offers on Earn come from EdgeCash’s offers API for now. Add EdgeCash under Networks
                (its id or name must say EdgeCash), then connect its API here.
              </EmptyState>
            </div>
          )}
          {others.length > 0 && (
            <p className="text-[13px] leading-relaxed text-stone">
              {others.map((n) => n.name).join(', ')}: no offers API we can read yet, so{' '}
              {others.length === 1 ? 'it stays' : 'they stay'} on{' '}
              {others.length === 1 ? 'its wall' : 'their walls'}.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Offers tab */

const SORTS = [
  { key: 'recent', label: 'Recently started' },
  { key: 'starts', label: 'Most started' },
  { key: 'earners', label: 'Most members paid' },
  { key: 'problems', label: 'Most missing coins' },
  { key: 'used', label: 'Most “had it before”' },
  { key: 'downs', label: 'Most thumbs down' },
] as const;

function OfferList({ networks }: { networks: Network[] }) {
  const uid = useId();
  const [q, setQ] = useState('');
  const [provider, setProvider] = useState('');
  const [sort, setSort] = useState<(typeof SORTS)[number]['key']>('recent');
  const [status, setStatus] = useState<'all' | 'hidden' | 'boosted'>('all');
  const [open, setOpen] = useState<{ provider: string; ref: string } | null>(null);
  const query = useDebounced(q.trim(), 300);
  const search = new URLSearchParams({ sort, status });
  if (provider) search.set('provider', provider);
  if (query) search.set('q', query);
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['offers', 'list', search.toString()],
    queryFn: () => api<{ offers: OfferRow[] }>(`/offers?${search}`),
  });
  const rows = data?.offers ?? [];
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={q}
          onChange={setQ}
          placeholder="Offer name, offer id or app id"
          className="w-full sm:w-72"
        />
        {networks.length > 1 && (
          <select
            aria-label="Network"
            className="input min-h-10 w-auto py-0 text-[14px]"
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
          >
            <option value="">All networks</option>
            {networks.map((n) => (
              <option key={n.id} value={n.id}>
                {n.name}
              </option>
            ))}
          </select>
        )}
        <label htmlFor={`${uid}-sort`} className="sr-only">
          Sort
        </label>
        <select
          id={`${uid}-sort`}
          className="input min-h-10 w-auto py-0 text-[14px]"
          value={sort}
          onChange={(e) => setSort(e.target.value as typeof sort)}
        >
          {SORTS.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>
        <div className="sm:ml-auto">
          <Tabs
            label="Staff changes"
            value={status}
            onChange={setStatus}
            items={[
              { key: 'all', label: 'All' },
              { key: 'hidden', label: 'Hidden' },
              { key: 'boosted', label: 'Moved' },
            ]}
          />
        </div>
      </div>
      <ErrorNote error={error} onRetry={() => void refetch()} />
      <div className="card overflow-hidden">
        {isLoading ? (
          <SkeletonRows />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Sparkles}
            title={query || status !== 'all' ? 'Nothing matches' : 'No offers started yet'}
          >
            {query || status !== 'all'
              ? 'Try another name, or clear the filters.'
              : 'Offers show here once members start them from Earn or say they had the app before. To see what members are offered now, use Setup → Preview.'}
          </EmptyState>
        ) : (
          <>
            <ul className="divide-y divide-hairline sm:hidden">
              {rows.map((r) => (
                <li key={`${r.provider}|${r.ref}`}>
                  <button
                    type="button"
                    onClick={() => setOpen({ provider: r.provider, ref: r.ref })}
                    className="flex w-full items-start gap-3 px-4 py-3 text-left active:bg-canvas"
                  >
                    <OfferIcon src={r.iconUrl} name={r.title} />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-start justify-between gap-2">
                        <span className="min-w-0 truncate text-[15px] font-semibold">
                          {r.title}
                        </span>
                        <span className="shrink-0 text-[14px] font-bold tabular-nums">
                          {fmtNum(r.coins)}
                        </span>
                      </span>
                      <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[12px] text-stone">
                        {[
                          r.category ? (CATEGORY[r.category] ?? r.category) : null,
                          r.platform ? (PLATFORM[r.platform] ?? r.platform) : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                        {r.hidden && <Pill tone="berry">Hidden</Pill>}
                        {!r.hidden && boostLabel(r.boost) && (
                          <Pill tone="sky">{boostLabel(r.boost)}</Pill>
                        )}
                      </span>
                      <span className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-graphite">
                        <span>
                          <b className="tabular-nums">{fmtNum(r.stats.starts)}</b> started
                        </span>
                        <span>
                          <b className="tabular-nums">{fmtNum(r.stats.earners)}</b> got coins
                        </span>
                        <Rating ups={r.stats.ups} downs={r.stats.downs} />
                        {r.stats.problems > 0 && (
                          <span className="font-semibold text-berry-deep">
                            {fmtNum(r.stats.problems)} missing
                          </span>
                        )}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            <div className="hidden sm:block">
              <ScrollX>
                <table className="data-table table-compact min-w-[920px] table-fixed [&_td]:align-middle">
                  <colgroup>
                    <col />
                    <col className="w-[104px]" />
                    <col className="w-[96px]" />
                    <col className="w-[112px]" />
                    <col className="w-[104px]" />
                    <col className="w-[92px]" />
                    <col className="w-[92px]" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Offer</th>
                      <th className="text-right">Pays</th>
                      <th className="text-right" title="Starts in the last 30 days (members)">
                        Started
                      </th>
                      <th className="text-right" title="Members who started it here and got coins">
                        Got coins
                      </th>
                      <th className="text-right">Rating</th>
                      <th className="text-right" title="Thumbs down saying the coins never came">
                        Missing
                      </th>
                      <th className="text-right" title="Said they had the app before (90 days)">
                        Had it
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr
                        key={`${r.provider}|${r.ref}`}
                        className="cursor-pointer"
                        onClick={() => setOpen({ provider: r.provider, ref: r.ref })}
                      >
                        <td>
                          <div className="flex min-w-0 items-center gap-3">
                            <OfferIcon src={r.iconUrl} name={r.title} />
                            <div className="min-w-0">
                              <button
                                type="button"
                                className="block max-w-full truncate text-left font-semibold underline-offset-2 hover:underline"
                                title={r.title}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpen({ provider: r.provider, ref: r.ref });
                                }}
                              >
                                {r.title}
                              </button>
                              <div className="flex min-w-0 items-center gap-1.5 text-[12px] text-stone">
                                <span className="truncate">
                                  {[
                                    networks.length > 1 ? r.network : null,
                                    r.category ? (CATEGORY[r.category] ?? r.category) : null,
                                    r.platform ? (PLATFORM[r.platform] ?? r.platform) : null,
                                  ]
                                    .filter(Boolean)
                                    .join(' · ')}
                                </span>
                                {r.hidden && <Pill tone="berry">Hidden</Pill>}
                                {!r.hidden && boostLabel(r.boost) && (
                                  <Pill tone="sky">{boostLabel(r.boost)}</Pill>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="text-right">
                          <div className="font-semibold">{fmtNum(r.coins)}</div>
                          <div className="text-[12px] text-stone">{fmtUsd(r.usd)}</div>
                        </td>
                        <td className="text-right">
                          <div className="font-semibold">{fmtNum(r.stats.starts)}</div>
                          <div className="text-[12px] text-stone">
                            {fmtNum(r.stats.starters)} member{r.stats.starters === 1 ? '' : 's'}
                          </div>
                        </td>
                        <td className="text-right">
                          <div className="font-semibold">{fmtNum(r.stats.earners)}</div>
                          <div className="text-[12px] text-stone">
                            {pct(r.stats.earners, r.stats.starters)} of starters
                          </div>
                        </td>
                        <td className="text-right text-[13px]">
                          <Rating ups={r.stats.ups} downs={r.stats.downs} />
                        </td>
                        <td
                          className={`text-right font-semibold ${r.stats.problems ? 'text-berry-deep' : 'text-stone'}`}
                        >
                          {fmtNum(r.stats.problems)}
                        </td>
                        <td
                          className={`text-right font-semibold ${r.stats.usedBefore ? '' : 'text-stone'}`}
                        >
                          {fmtNum(r.stats.usedBefore)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollX>
            </div>
          </>
        )}
      </div>
      {open && (
        <OfferDrawer provider={open.provider} offerRef={open.ref} onClose={() => setOpen(null)} />
      )}
    </>
  );
}

function OfferIcon({ src, name, size = 40 }: { src: string | null; name: string; size?: number }) {
  return src ? (
    <img
      src={src}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      className="shrink-0 rounded-[10px] border border-hairline bg-canvas object-cover"
      style={{ width: size, height: size }}
    />
  ) : (
    <Initial name={name} size={size} />
  );
}

function Rating({ ups, downs }: { ups: number; downs: number }) {
  if (!ups && !downs) return <span className="text-stone">—</span>;
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap font-semibold tabular-nums">
      <span className="inline-flex items-center gap-1 text-mint-deep">
        <ThumbsUp size={13} aria-label="Thumbs up" />
        {fmtNum(ups)}
      </span>
      <span
        className={`inline-flex items-center gap-1 ${downs ? 'text-berry-deep' : 'text-stone'}`}
      >
        <ThumbsDown size={13} aria-label="Thumbs down" />
        {fmtNum(downs)}
      </span>
    </span>
  );
}

function OfferDrawer({
  provider,
  offerRef,
  onClose,
}: {
  provider: string;
  offerRef: string;
  onClose: () => void;
}) {
  const { data, error, isLoading } = useQuery({
    queryKey: ['offers', 'one', provider, offerRef],
    queryFn: () =>
      api<OfferDetail>(`/offers/${encodeURIComponent(provider)}/${encodeURIComponent(offerRef)}`),
  });
  const o = data?.offer;
  const s = data?.stats;
  return (
    <Drawer
      onClose={onClose}
      title={o?.title ?? 'Offer'}
      subtitle={
        o ? (
          <span className="[overflow-wrap:anywhere]">
            {o.network} · offer {o.ref}
            {o.packageId ? ` · ${o.packageId}` : ''}
          </span>
        ) : undefined
      }
    >
      <ErrorNote error={error} />
      {isLoading || !data || !o ? (
        <SkeletonRows rows={4} />
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat label="Started (30 days)" value={fmtNum(s?.starts ?? 0)}>
              {fmtNum(s?.starters ?? 0)} members
            </Stat>
            <Stat label="Got coins" value={fmtNum(s?.earners ?? 0)}>
              {pct(s?.earners ?? 0, s?.starters ?? 0)} of starters
            </Stat>
            <Stat
              label="Coins never came"
              value={fmtNum(s?.problems ?? 0)}
              tone={s?.problems ? 'bad' : undefined}
            >
              of {fmtNum((s?.ups ?? 0) + (s?.downs ?? 0))} verdicts
            </Stat>
            <Stat label="Had it before" value={fmtNum(data.usedBefore90d)}>
              last 90 days
            </Stat>
          </div>

          <Card title="Staff’s say">
            <OverrideForm provider={provider} offerRef={offerRef} initial={data.override} />
          </Card>

          <Card title="Steps" pad={false}>
            <table className="data-table table-compact">
              <thead>
                <tr>
                  <th>When the member…</th>
                  <th className="text-right">Network pays</th>
                  <th className="text-right">Member gets</th>
                </tr>
              </thead>
              <tbody>
                {o.steps.map((st) => (
                  <tr key={st.id}>
                    <td className="text-[13px] [overflow-wrap:anywhere]">{st.label}</td>
                    <td className="text-right text-[13px]">{fmtUsd(st.usd)}</td>
                    <td className="text-right text-[13px] font-semibold">{fmtNum(st.coins)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="border-t border-hairline px-5 py-3 text-[12px] text-stone">
              As the offer looked when a member last acted on it, <TimeAgo t={o.lastAt} />. Step
              names are the network’s; members read them rewritten and in their language.
            </p>
          </Card>

          <Card title="Credits, last 90 days" pad={false}>
            {data.credits.length === 0 ? (
              <p className="px-5 py-4 text-[13px] text-stone">
                No postback has credited this offer yet. Credits are matched by the network’s offer
                id: check Setup if members say they finished it.
              </p>
            ) : (
              <table className="data-table table-compact">
                <thead>
                  <tr>
                    <th>Event</th>
                    <th className="text-right">Credits</th>
                    <th className="text-right">Coins</th>
                    <th className="text-right">Taken back</th>
                  </tr>
                </thead>
                <tbody>
                  {data.credits.map((c) => (
                    <tr key={c.event || '—'}>
                      <td className="text-[13px] [overflow-wrap:anywhere]">{c.event || '—'}</td>
                      <td className="text-right text-[13px]">{fmtNum(c.n)}</td>
                      <td className="text-right text-[13px]">{fmtNum(c.coins)}</td>
                      <td
                        className={`text-right text-[13px] ${c.reversed ? 'font-semibold text-berry-deep' : 'text-stone'}`}
                      >
                        {fmtNum(c.reversed)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          <Card title={`What members said (${data.feedback.length})`} pad={false}>
            {data.feedback.length === 0 ? (
              <p className="px-5 py-4 text-[13px] text-stone">
                Nobody has rated it yet. Members can rate an offer once they’ve started it.
              </p>
            ) : (
              <ul className="divide-y divide-hairline">
                {data.feedback.map((f) => (
                  <li key={f.userId} className="flex gap-3 px-5 py-3">
                    <span
                      className={`mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full ${f.verdict === 'up' ? 'bg-mint/15 text-mint-deep' : 'bg-berry/10 text-berry-deep'}`}
                    >
                      {f.verdict === 'up' ? (
                        <ThumbsUp size={14} aria-label="Thumbs up" />
                      ) : (
                        <ThumbsDown size={14} aria-label="Thumbs down" />
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
                        <Link
                          to={`/users/${f.userId}`}
                          className="font-semibold underline-offset-2 hover:underline"
                        >
                          {f.username ?? f.userId}
                        </Link>
                        <span className="text-stone">
                          <TimeAgo t={f.updatedAt} />
                        </span>
                      </div>
                      {f.reasons.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {f.reasons.map((r) => (
                            <Pill key={r} tone={PROBLEM_REASONS.has(r) ? 'berry' : 'neutral'}>
                              {REASON[r] ?? r}
                            </Pill>
                          ))}
                        </div>
                      )}
                      {f.note && (
                        <p className="mt-1.5 whitespace-pre-line text-[13px] leading-relaxed text-graphite [overflow-wrap:anywhere]">
                          {f.note}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}
    </Drawer>
  );
}

function Stat({
  label,
  value,
  tone,
  children,
}: {
  label: string;
  value: string;
  tone?: 'bad' | undefined;
  children?: ReactNode;
}) {
  return (
    <div className="rounded-[12px] border border-hairline bg-paper px-3.5 py-3">
      <div className="text-[12px] font-semibold text-stone">{label}</div>
      <div
        className={`mt-1 text-[22px] font-extrabold leading-none tracking-[-0.02em] tabular-nums ${tone === 'bad' ? 'text-berry-deep' : ''}`}
      >
        {value}
      </div>
      {children && <div className="mt-1 text-[12px] text-stone">{children}</div>}
    </div>
  );
}

/** Hide for everyone, or move up or down the list. Saving the defaults removes staff's say. */
function OverrideForm({
  provider,
  offerRef,
  initial,
  onSaved,
}: {
  provider: string;
  offerRef: string;
  initial: { hidden: boolean; boost: number; note: string | null } | null;
  onSaved?: (v: { hidden: boolean; boost: number; note: string | null }) => void;
}) {
  const uid = useId();
  const qc = useQueryClient();
  const can = useCan();
  const manage = can('providers.manage');
  const [hidden, setHidden] = useState(initial?.hidden ?? false);
  const [boost, setBoost] = useState(String(initial?.boost ?? 1));
  const [note, setNote] = useState(initial?.note ?? '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const known = BOOSTS.some((b) => b.key === boost);
  const dirty =
    hidden !== (initial?.hidden ?? false) ||
    Number(boost) !== (initial?.boost ?? 1) ||
    note.trim() !== (initial?.note ?? '');
  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      await api(`/offers/${encodeURIComponent(provider)}/${encodeURIComponent(offerRef)}`, {
        method: 'PUT',
        body: { hidden, boost: Number(boost), note: note.trim() },
      });
      await qc.invalidateQueries({ queryKey: ['offers'] });
      onSaved?.({ hidden, boost: Number(boost), note: note.trim() || null });
      toast(hidden ? 'Hidden from every member.' : 'Saved. Members see it on their next visit.');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-4">
      <div className="grid gap-4">
        <Field label="On Earn">
          <Segmented
            label="On Earn"
            value={hidden ? 'hidden' : 'shown'}
            onChange={(k) => manage && setHidden(k === 'hidden')}
            options={[
              { key: 'shown', label: 'Shown' },
              {
                key: 'hidden',
                label: 'Hidden for everyone',
                activeClass: 'bg-berry text-white',
              },
            ]}
          />
        </Field>
        <Field label="Place in the list">
          <Segmented
            label="Place in the list"
            value={known ? boost : '1'}
            onChange={(k) => manage && setBoost(k)}
            options={BOOSTS.map((b) => ({ key: b.key, label: b.label }))}
          />
        </Field>
      </div>
      <Field id={`${uid}-note`} label="Note for staff" hint="Why, for whoever looks next.">
        <input
          id={`${uid}-note`}
          className="input h-11 py-0"
          maxLength={500}
          value={note}
          disabled={!manage}
          placeholder="e.g. members say it never tracks on Android 14"
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>
      {err && (
        <p role="alert" className="text-[13px] font-semibold text-berry-deep">
          {err}
        </p>
      )}
      {manage && (
        <button
          className="btn-primary h-10 py-0 text-[14px]"
          disabled={!dirty || saving}
          onClick={save}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Setup tab */

function NetworkSetup({ n }: { n: Network }) {
  return (
    <section className="card" aria-label={n.name}>
      <div className="flex items-center gap-3 border-b border-hairline px-4 py-4 sm:px-5">
        {n.logoUrl ? (
          <img
            src={n.logoUrl}
            alt=""
            className="h-11 w-11 shrink-0 rounded-[12px] border border-hairline bg-paper object-contain p-1"
          />
        ) : (
          <Initial name={n.name} size={44} />
        )}
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-[17px] font-extrabold tracking-[-0.01em]">{n.name}</h2>
          <div className="text-[13px] text-stone">
            {n.api?.enabled
              ? n.status === 'active'
                ? 'Offers on Earn: on'
                : `Offers on Earn: on, but the network is ${n.status}, so members see none`
              : 'Offers on Earn: off'}
          </div>
        </div>
        <Pill tone={n.api?.enabled && n.status === 'active' ? 'mint' : 'neutral'}>
          {n.api?.enabled && n.status === 'active' ? 'Live' : 'Off'}
        </Pill>
      </div>
      <div className="grid gap-6 p-4 sm:p-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <ApiForm n={n} />
        <Checks n={n} />
      </div>
      {n.api?.hasKey && <PreviewPanel n={n} />}
    </section>
  );
}

function ApiForm({ n }: { n: Network }) {
  const uid = useId();
  const qc = useQueryClient();
  const can = useCan();
  const manage = can('providers.manage');
  const secrets = can('providers.secrets');
  const a = n.api;
  const [enabled, setEnabled] = useState(a?.enabled ?? false);
  const [publisherId, setPublisherId] = useState(a?.publisherId ?? '');
  const [key, setKey] = useState('');
  const [supportUrl, setSupportUrl] = useState(a?.supportUrl ?? '');
  const [casino, setCasino] = useState(a?.casino ?? false);
  // Blank: the network's own coins per $1.
  const [rate, setRate] = useState(a?.coinsPerUsd ? String(a.coinsPerUsd) : '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const dirty =
    enabled !== (a?.enabled ?? false) ||
    publisherId.trim() !== (a?.publisherId ?? '') ||
    !!key.trim() ||
    supportUrl.trim() !== (a?.supportUrl ?? '') ||
    casino !== (a?.casino ?? false) ||
    rate !== (a?.coinsPerUsd ? String(a.coinsPerUsd) : '');
  const save = async () => {
    if (rate && !/^[1-9]\d{0,6}$/.test(rate))
      return setErr('Coins per $1: a whole number, or blank for the network’s rate.');
    setSaving(true);
    setErr(null);
    try {
      await api(`/offers/networks/${encodeURIComponent(n.id)}`, {
        method: 'PUT',
        body: {
          adapter: 'edgecash',
          enabled,
          publisherId: publisherId.trim(),
          ...(key.trim() ? { key: key.trim() } : {}),
          supportUrl: supportUrl.trim(),
          casino,
          coinsPerUsd: rate ? Number(rate) : null,
        },
      });
      setKey('');
      await qc.invalidateQueries({ queryKey: ['offers'] });
      await qc.invalidateQueries({ queryKey: ['providers'] });
      toast(enabled ? 'Saved. Members see these offers on their next visit.' : 'Saved.');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  };
  const locked = !manage;
  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-[15px] font-bold">Offers API</h3>
        <p className="mt-0.5 text-[13px] leading-snug text-stone">
          EdgeCash. The key is stored encrypted and never shown again; paste a new one to replace
          it. Changing the account needs “Reveal network keys”.
        </p>
      </div>
      <Field label="Offers on Earn">
        <Segmented
          label="Offers on Earn"
          value={enabled ? 'on' : 'off'}
          onChange={(k) => !locked && setEnabled(k === 'on')}
          options={[
            { key: 'off', label: 'Off' },
            {
              key: 'on',
              label: 'On',
              activeClass: 'bg-mint text-ink shadow-[0_1px_3px_rgba(17,17,16,0.12)]',
            },
          ]}
        />
      </Field>
      <div className="grid gap-x-4 gap-y-5 sm:grid-cols-2">
        <Field id={`${uid}-pub`} label="Publisher id">
          <input
            id={`${uid}-pub`}
            className="input h-11 py-0 font-mono text-[14px]"
            value={publisherId}
            disabled={locked || !secrets}
            spellCheck={false}
            autoComplete="off"
            onChange={(e) => setPublisherId(e.target.value)}
          />
        </Field>
        <Field
          id={`${uid}-key`}
          label={
            <span className="inline-flex items-center gap-1.5">
              <KeyRound size={13} aria-hidden="true" /> API key
            </span>
          }
          hint={a?.hasKey ? 'A key is saved.' : 'No key saved yet.'}
        >
          <input
            id={`${uid}-key`}
            type="password"
            className="input h-11 py-0 font-mono text-[14px]"
            value={key}
            disabled={locked || !secrets}
            placeholder={a?.hasKey ? 'Paste to replace' : 'Paste the API key'}
            autoComplete="new-password"
            spellCheck={false}
            onChange={(e) => setKey(e.target.value)}
          />
        </Field>
      </div>
      <Field
        id={`${uid}-sup`}
        label="Problem report link"
        hint={
          <>
            Where a member reports one offer. Blank uses the network’s:{' '}
            <code className="text-[12px] [overflow-wrap:anywhere]">{n.defaultSupportUrl}</code>
          </>
        }
      >
        <input
          id={`${uid}-sup`}
          className="input h-11 py-0 font-mono text-[13px]"
          value={supportUrl}
          disabled={locked}
          placeholder="https://…{offer_id}…{user_id}"
          spellCheck={false}
          onChange={(e) => setSupportUrl(e.target.value)}
        />
      </Field>
      <Field
        id={`${uid}-rate`}
        label="Coins per $1 on Earn"
        hint={`What a member gets for each $1 an offer lists, as the postback will credit it: the rate set in the network’s dashboard. Blank uses the network’s ${fmtNum(n.checks.networkCoinsPerUsd)}. The checks beside compare it with past credits.`}
      >
        <input
          id={`${uid}-rate`}
          className="input h-11 w-40 py-0 font-semibold tabular-nums"
          inputMode="numeric"
          value={rate}
          disabled={locked}
          placeholder={String(n.checks.networkCoinsPerUsd)}
          onChange={(e) => setRate(e.target.value.replace(/\D/g, ''))}
        />
      </Field>
      <Field
        label="Casino offers"
        hint="Website on a computer only. Never in the app or on phones: Google Play restricts gambling ads."
      >
        <Segmented
          label="Casino offers"
          value={casino ? 'desktop' : 'never'}
          onChange={(k) => !locked && setCasino(k === 'desktop')}
          options={[
            { key: 'never', label: 'Never' },
            { key: 'desktop', label: 'On computers' },
          ]}
        />
      </Field>
      {err && (
        <p
          role="alert"
          className="flex items-start gap-2 rounded-[10px] bg-berry/[0.06] px-3 py-2 text-[13px] font-semibold text-berry-deep"
        >
          <CircleAlert size={15} className="mt-px shrink-0" aria-hidden="true" />
          {err}
        </p>
      )}
      {manage && (
        <button className="btn-primary h-11 py-0" disabled={!dirty || saving} onClick={save}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      )}
    </div>
  );
}

function CheckRow({
  state,
  title,
  children,
}: {
  state: 'ok' | 'warn' | 'bad' | 'off';
  title: ReactNode;
  children?: ReactNode;
}) {
  const icon =
    state === 'ok' ? (
      <Check size={14} aria-label="OK" />
    ) : state === 'off' ? (
      <Minus size={14} aria-label="Not set" />
    ) : (
      <TriangleAlert size={14} aria-label={state === 'bad' ? 'Problem' : 'Check this'} />
    );
  const tone =
    state === 'ok'
      ? 'bg-mint/15 text-mint-deep'
      : state === 'bad'
        ? 'bg-berry/10 text-berry-deep'
        : state === 'warn'
          ? 'bg-coin-tint text-coin-ink'
          : 'bg-ink/[0.06] text-graphite';
  return (
    <li className="flex gap-3 py-3">
      <span className={`mt-px grid h-6 w-6 shrink-0 place-items-center rounded-full ${tone}`}>
        {icon}
      </span>
      <div className="min-w-0">
        <div className="text-[14px] font-semibold leading-snug">{title}</div>
        {children && <div className="mt-0.5 text-[13px] leading-snug text-stone">{children}</div>}
      </div>
    </li>
  );
}

/** Whether what Earn shows will be what postbacks credit. */
function Checks({ n }: { n: Network }) {
  const c = n.checks;
  const off = c.observed ? Math.abs(c.observed.coinsPerUsd - c.coinsPerUsd) / c.coinsPerUsd : 0;
  return (
    <div>
      <h3 className="text-[15px] font-bold">Checks</h3>
      <p className="mt-0.5 text-[13px] leading-snug text-stone">
        Earn shows each step at {fmtNum(c.coinsPerUsd)} coins per $1 (Coins per $1 on Earn).
        Postbacks credit what the network sends.
      </p>
      <ul className="mt-2 divide-y divide-hairline">
        {c.observed ? (
          <CheckRow
            state={off > 0.03 ? 'bad' : 'ok'}
            title={
              off > 0.03
                ? `Postbacks credit about ${fmtNum(c.observed.coinsPerUsd)} coins per $1, Earn shows ${fmtNum(c.coinsPerUsd)}`
                : `Postbacks credit ${fmtNum(c.observed.coinsPerUsd)} coins per $1, as Earn shows`
            }
          >
            From {fmtNum(c.observed.credits)} credits in 60 days.
            {off > 0.03 &&
              ' Members would be promised a different amount than they get: set “Coins per $1 on Earn” to the rate in the network’s dashboard before turning offers on.'}
          </CheckRow>
        ) : (
          <CheckRow
            state={c.postbackRevenue ? 'warn' : 'bad'}
            title="Can’t compare coins with payouts yet"
          >
            {c.postbackRevenue
              ? 'No credit with a payout in 60 days. Compare the rate in the network’s dashboard with coins per $1 by hand.'
              : 'Postbacks don’t send the payout to us. Map “Payout to us (USD)” in the postback setup, so this can be checked.'}
          </CheckRow>
        )}
        <CheckRow
          state={c.postbackOfferId ? 'ok' : 'bad'}
          title={
            c.postbackOfferId ? 'Postbacks send the offer id' : 'Postbacks don’t send the offer id'
          }
        >
          {c.postbackOfferId
            ? `Credits are matched to the offer started on Earn. ${fmtNum(c.creditsWithOffer30d)} of ${fmtNum(c.credits30d)} credits in 30 days had one.`
            : 'Without it, credits can’t be matched to offers: no “got coins” counts, history or ranking. Map “Offer ID” in the postback setup.'}
        </CheckRow>
        <CheckRow
          state={c.postbackClickId ? 'ok' : 'off'}
          title={
            c.postbackClickId ? 'Postbacks send our click id' : 'Click id not mapped (optional)'
          }
        >
          {c.postbackClickId
            ? 'Each credit is tied to the exact start.'
            : 'Each start sends our click id as subid2. Add {subid2} to the network’s postback and map it as “Click ID” to tie each credit to its start.'}
        </CheckRow>
        <CheckRow
          state={c.starts30d ? 'ok' : 'off'}
          title={`${fmtNum(c.starts30d)} starts from Earn in 30 days`}
        />
      </ul>
    </div>
  );
}

function PreviewPanel({ n }: { n: Network }) {
  const uid = useId();
  const [country, setCountry] = useState('US');
  const [device, setDevice] = useState<'android' | 'ios' | 'desktop'>('android');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<Preview | null>(null);
  const [open, setOpen] = useState<PreviewOffer | null>(null);
  const run = async () => {
    if (!/^[A-Za-z]{2}$/.test(country)) return setErr('Country: two letters, e.g. US or DE.');
    setBusy(true);
    setErr(null);
    try {
      setResult(
        await api<Preview>(`/offers/networks/${encodeURIComponent(n.id)}/preview`, {
          body: { country, device },
        }),
      );
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const patchLocal = (ref: string, v: { hidden: boolean; boost: number; note: string | null }) =>
    setResult((r) =>
      r ? { ...r, offers: r.offers.map((o) => (o.ref === ref ? { ...o, ...v } : o)) } : r,
    );
  return (
    <div className="border-t border-hairline p-4 sm:p-5">
      <h3 className="text-[15px] font-bold">Preview</h3>
      <p className="mt-0.5 text-[13px] leading-snug text-stone">
        What a new member sees now, ranked as Earn ranks it. Reads the network live with the saved
        key (a few seconds).
      </p>
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <div>
          <label htmlFor={`${uid}-c`} className="mb-1 block text-[12px] font-semibold text-stone">
            Country
          </label>
          <input
            id={`${uid}-c`}
            className="input h-10 w-20 py-0 font-mono text-[14px] uppercase"
            maxLength={2}
            value={country}
            onChange={(e) => setCountry(e.target.value.toUpperCase())}
          />
        </div>
        <Segmented
          label="Device"
          value={device}
          onChange={setDevice}
          options={[
            { key: 'android', label: 'Android' },
            { key: 'ios', label: 'iPhone' },
            { key: 'desktop', label: 'Computer' },
          ]}
        />
        <button className="btn-primary h-10 py-0 text-[14px]" disabled={busy} onClick={run}>
          {busy ? 'Reading…' : 'Preview'}
        </button>
      </div>
      {err && (
        <p role="alert" className="mt-3 text-[13px] font-semibold text-berry-deep">
          {err}
        </p>
      )}
      {result && (
        <div className="mt-4">
          <p className="text-[13px] text-graphite">
            <b>{fmtNum(result.offers.length)}</b> offers shown of {fmtNum(result.received)} the
            network sent, in {(result.ms / 1000).toFixed(1)}s
            {result.casinoHeld > 0 && ` · ${fmtNum(result.casinoHeld)} casino held back`}
          </p>
          {result.offers.length > 0 && (
            <ol className="mt-3 divide-y divide-hairline rounded-[12px] border border-hairline bg-paper">
              {result.offers.map((o, i) => (
                <li key={o.ref}>
                  <button
                    type="button"
                    onClick={() => setOpen(o)}
                    className={`flex w-full items-center gap-3 px-3.5 py-2.5 text-left transition hover:bg-canvas ${o.hidden ? 'opacity-55' : ''}`}
                  >
                    <span className="w-7 shrink-0 text-right text-[12px] font-semibold tabular-nums text-stone">
                      {i + 1}
                    </span>
                    <OfferIcon src={o.iconUrl} name={o.title} size={36} />
                    <span className="min-w-0 flex-1">
                      <span className="flex min-w-0 items-center gap-1.5">
                        <span className="truncate text-[14px] font-semibold">{o.title}</span>
                        {o.hidden && (
                          <EyeOff
                            size={13}
                            className="shrink-0 text-berry-deep"
                            aria-label="Hidden"
                          />
                        )}
                        {!o.hidden && o.boost > 1 && (
                          <ArrowUp
                            size={13}
                            className="shrink-0 text-sky-800"
                            aria-label="Moved up"
                          />
                        )}
                        {!o.hidden && o.boost < 1 && (
                          <ArrowDown
                            size={13}
                            className="shrink-0 text-stone"
                            aria-label="Moved down"
                          />
                        )}
                      </span>
                      <span className="block truncate text-[12px] text-stone">
                        {CATEGORY[o.category] ?? o.category} · {PLATFORM[o.platform] ?? o.platform}{' '}
                        · {o.steps.length} step{o.steps.length === 1 ? '' : 's'}
                        {o.stats
                          ? ` · ${fmtNum(o.stats.starts)} started, ${fmtNum(o.stats.earners)} paid`
                          : ''}
                      </span>
                    </span>
                    <span className="shrink-0 text-right">
                      <span className="block text-[14px] font-bold tabular-nums">
                        {fmtNum(o.coins)}
                      </span>
                      <span className="block text-[12px] tabular-nums text-stone">
                        {fmtUsd(o.usd)}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
      {open && (
        <Drawer
          onClose={() => setOpen(null)}
          title={open.title}
          subtitle={
            <span className="[overflow-wrap:anywhere]">
              {n.name} · offer {open.ref}
              {open.packageId ? ` · ${open.packageId}` : ''}
            </span>
          }
        >
          <div className="space-y-6">
            {open.about && (
              <p className="whitespace-pre-line text-[14px] leading-relaxed text-graphite">
                {open.about}
              </p>
            )}
            <Card title="Steps" pad={false}>
              <table className="data-table table-compact">
                <thead>
                  <tr>
                    <th>As members read it</th>
                    <th className="text-right">Pays</th>
                    <th className="text-right">Coins</th>
                  </tr>
                </thead>
                <tbody>
                  {open.steps.map((st, i) => (
                    <tr key={i}>
                      <td className="text-[13px] [overflow-wrap:anywhere]">
                        {st.label}
                        {st.source && st.source !== st.label && (
                          <div className="text-[12px] text-stone">Network: {st.source}</div>
                        )}
                        {st.minutes && (
                          <div className="text-[12px] text-stone">About {st.minutes} min</div>
                        )}
                      </td>
                      <td className="text-right text-[13px]">{fmtUsd(st.usd)}</td>
                      <td className="text-right text-[13px] font-semibold">{fmtNum(st.coins)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
            <Card title="Staff’s say">
              <OverrideForm
                provider={n.id}
                offerRef={open.ref}
                initial={{ hidden: open.hidden, boost: open.boost, note: open.note }}
                onSaved={(v) => {
                  patchLocal(open.ref, v);
                  setOpen({ ...open, ...v });
                }}
              />
            </Card>
          </div>
        </Drawer>
      )}
    </div>
  );
}
